import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Database, MapPin, Building2, Calendar, Activity } from 'lucide-react';
import { useForumTopic, useForumComments } from '../../hooks/useForum';
import CommentList from '../../components/forum/CommentList';
import CommentForm from '../../components/forum/CommentForm';
import Pagination from '../../components/ui/Pagination';
import type { ForumComment } from '../../types/forum';

const ForumTopicPage = () => {
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const { topic, isLoading: topicLoading } = useForumTopic(id);
  const { 
    comments, 
    totalPages, 
    isLoading: commentsLoading 
  } = useForumComments(id, currentPage);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [localComments, setLocalComments] = useState<ForumComment[]>([]);

  useEffect(() => {
    if (comments) {
      setLocalComments(comments);
    }
  }, [comments]);

  const handleCommentSuccess = (newComment: ForumComment) => {
    setLocalComments(prev => [...prev, newComment]);
    setReplyTo(null);
  };

  if (topicLoading) {
    return (
      <div className="text-center py-8 text-content-secondary">
        Loading topic...
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="text-center py-8 text-content-secondary">
        Topic not found
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Navigation Links */}
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/community/comments"
          className="inline-flex items-center text-brand hover:text-brand-light"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Comment Hub
        </Link>
        
        {topic.animatronic && (
          <Link
            to={`/database/${topic.animatronic.id}`}
            className="inline-flex items-center text-brand hover:text-brand-light"
          >
            <Database className="w-4 h-4 mr-2" />
            View Animatronic Details
          </Link>
        )}
      </div>

      {/* Topic Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-4">{topic.title}</h1>
        {topic.animatronic && (
          <>
            <div className="flex gap-6 mb-4">
              {/* Thumbnail */}
              {topic.animatronic.media?.[0] && (
                <div className="hidden sm:block flex-shrink-0 w-48 h-32">
                  <img
                    src={topic.animatronic.media[0].url}
                    alt={topic.animatronic.name}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              )}
              
              <div className="flex-grow">
                <p className="text-content-secondary mb-4">
                  Discussing: {topic.animatronic.name}
                </p>
                <div className="flex flex-wrap gap-4 text-sm text-content-secondary">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1.5 text-brand/70" />
                    <span>{topic.animatronic.country}</span>
                  </div>
                  <div className="flex items-center">
                    <Building2 className="w-4 h-4 mr-1.5 text-brand/70" />
                    <span>{topic.animatronic.park}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1.5 text-brand/70" />
                    <span>{topic.animatronic.year}</span>
                  </div>
                  <div className="flex items-center">
                    <Activity className="w-4 h-4 mr-1.5 text-brand/70" />
                    <span className="capitalize">{topic.animatronic.status}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <div className="flex items-center text-sm text-content-secondary mt-4">
          <span>Started {new Date(topic.created_at).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Comments */}
      {commentsLoading ? (
        <div className="text-center py-8 text-content-secondary">
          Loading comments...
        </div>
      ) : (
        <>
          <CommentList
            comments={localComments}
            onReply={setReplyTo}
          />
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="my-8">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}

          {/* Comment Form */}
          <div className="mt-8">
            <CommentForm
              topicId={id!}
              replyTo={replyTo}
              onCancel={() => setReplyTo(null)}
              onSuccess={handleCommentSuccess}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ForumTopicPage;